import { useEffect, useRef, useState } from 'react'
import {
  bootIntercomAsUser,
  bootIntercomAsVisitor,
  shutdownIntercom,
  IntercomUser
} from 'tools/intercom'

export interface IntercomBridgeProps {
  user: IntercomUser | null
  authReady: boolean
  // Cob/widget routes (see appContainer) are always-unauthenticated onboarding entry points —
  // a stale console session in localStorage must never drive Intercom's boot decision there.
  isWidgetRoute: boolean
}

interface PendingBoot {
  type: 'user' | 'visitor'
  user?: IntercomUser
}

const IntercomBridge: React.FC<IntercomBridgeProps> = ({ user, authReady, isWidgetRoute }) => {
  const previousUserIdRef = useRef<string | number | null>(null)
  const isInitializedRef = useRef<boolean>(false)
  const [shouldShutdown, setShouldShutdown] = useState<boolean>(false)
  const [pendingBoot, setPendingBoot] = useState<PendingBoot | null>(null)

  useEffect(() => {
    if (!shouldShutdown) {
      return
    }

    shutdownIntercom()
    
    const timer = setTimeout(() => {
      setShouldShutdown(false)
    }, 150)

    return () => clearTimeout(timer)
  }, [shouldShutdown])

  useEffect(() => {
    if (shouldShutdown || !pendingBoot) {
      return
    }

    const bootIntercom = async () => {
      try {
        if (pendingBoot.type === 'user' && pendingBoot.user) {
          await bootIntercomAsUser(pendingBoot.user)
        } else {
          await bootIntercomAsVisitor()
        }
      } catch (error) {
        console.error(`Failed to boot Intercom as ${pendingBoot.type}:`, error)
      } finally {
        setPendingBoot(null)
      }
    }

    bootIntercom()
  }, [shouldShutdown, pendingBoot])

  useEffect(() => {
    if (!authReady) {
      return
    }

    // Never let a Hub console session drive the boot decision on cob/widget routes.
    const effectiveUser = isWidgetRoute ? null : user

    const currentUserId = effectiveUser?.user_id ?? null
    const userChanged = previousUserIdRef.current !== currentUserId

    if (isInitializedRef.current && !userChanged) {
      return
    }

    previousUserIdRef.current = currentUserId
    isInitializedRef.current = true

    setShouldShutdown(true)

    if (effectiveUser) {
      // Every authenticated session boots identified (with user_id), regardless of
      // intercom_bucket -- Intercom's Messenger SDK classifies any boot() call carrying an
      // email as Type: User, and never allows demoting an existing User back to Lead. There is
      // no way to make a carrier/agent console login land as Type: Lead client-side. intercom_bucket
      // still rides along as a custom attribute (via the generic spread in buildBootConfig) so
      // non-paying accounts remain filterable/discardable in Intercom despite the Type mismatch.
      setPendingBoot({ type: 'user', user: effectiveUser })
    } else {
      setPendingBoot({ type: 'visitor' })
    }
  }, [user, authReady, isWidgetRoute])

  useEffect(() => {
    return () => {
      shutdownIntercom()
    }
  }, [])

  return null
}

export default IntercomBridge
